import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button, Card, Col, Form, Input, InputNumber, Row, Space, Typography, message, Upload, Modal, Radio, Checkbox, DatePicker } from 'antd';
import type { UploadProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useGetTNByIdQuery, useUpdateTNMutation, useCreateTNMutation, TNItemDto } from '../../store/api/tnApi';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';
import { useGetDriversQuery, useGetVehiclesQuery, useGetCarriersQuery, useGetContractorsByRoleQuery } from '../../store/api/referencesApi';
import CreateContractorModal, {
  CONTRACTOR_DEFAULT_ROLES_CLIENT,
  CONTRACTOR_DEFAULT_ROLES_CONSIGNEE,
  CONTRACTOR_DEFAULT_ROLES_SHIPPER,
} from '../../components/common/CreateContractorModal';
import AddressAutocomplete from '../../components/common/AddressAutocomplete';
import SmartSelect from '../../components/common/SmartSelect';
import CreateDriverModal from '../../components/common/CreateDriverModal';
import CreateCarrierModal from '../../components/common/CreateCarrierModal';
import VehicleSelectInline from '../../components/common/VehicleSelectInline';
import { computeNextVehiclesFromDriverChange } from '../../utils/driverDefaultVehicles';
import type { Driver } from '../../types';
import { routesApi } from '../../store/api/routesApi';
import { applicationsApi } from '../../store/api/applicationsApi';
import { useGetWarehousesQuery } from '../../store/api/referencesApi';
import { ApplicationCell, DataTable } from '../../ui';

const { Title, Text } = Typography;

const TNEditPage: React.FC = () => {
	const params = useParams();
	const id = Number(params.id);
	const isNew = !params.id;
	const navigate = useNavigate();
	const location = useLocation();
	const isViewMode = !isNew && location.pathname.endsWith('/view');

    const { data: tn, isLoading, refetch } = useGetTNByIdQuery(id, { skip: isNew || !Number.isFinite(id) });
	const [updateTN] = useUpdateTNMutation();
	const [createTN] = useCreateTNMutation();

	const { data: shippers } = useGetContractorsByRoleQuery('shipper' as any);
	const { data: consignees } = useGetContractorsByRoleQuery('consignee' as any);
	const { data: clients } = useGetContractorsByRoleQuery('client' as any);
	const { data: carriers } = useGetCarriersQuery();
	const { data: drivers } = useGetDriversQuery();
	const { data: vehicles, refetch: refetchVehicles } = useGetVehiclesQuery();
	const trucks = useMemo(
		() => (vehicles || []).filter((v) => v.vehicleCategory === 'TRUCK'),
		[vehicles]
	);
	const trailers = useMemo(
		() => (vehicles || []).filter((v) => v.vehicleCategory === 'TRAILER'),
		[vehicles]
	);

	// Контроль открытия выпадающих списков, чтобы скрывать их при открытии модалок создания
	const [carrierDropdownOpen, setCarrierDropdownOpen] = useState(false);
	const [driverDropdownOpen, setDriverDropdownOpen] = useState(false);
	const [prevDriver, setPrevDriver] = useState<Driver | null>(null);
	const prevDriverSyncedForTnId = useRef<number | null>(null);

    const [form] = Form.useForm();
    const [partnerModalOpen, setPartnerModalOpen] = React.useState(false);
    const [partnerForm] = Form.useForm();
	const [items, setItems] = useState<TNItemDto[]>([]);

	// Автогенерация номера по выбранной дате через backend
	const handleDateChange = async (date: any) => {
		if (!date) return;
		try {
			const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
			const res = await fetch(`${API_BASE_URL}/partner-applications/next-number?date=${encodeURIComponent(date.format('DD.MM.YYYY'))}`, {
				headers: token ? { Authorization: `Bearer ${token}` } : undefined,
				credentials: 'include'
			});
			if (res.ok) {
				const data = await res.json();
				if (data && data.number) {
					partnerForm.setFieldsValue({ applicationNumber: data.number });
				}
			}
		} catch {}
	};

    // Экспорт заявки партнёра в DOCX
    const handlePartnerDocxExport = async () => {
        try {
            const values = await partnerForm.validateFields();
            const queryParams = new URLSearchParams();

            Object.keys(values).forEach(key => {
                if (values[key] !== undefined && values[key] !== null) {
                    if (dayjs.isDayjs(values[key])) {
                        queryParams.append(key, values[key].format('DD.MM.YYYY'));
                    } else {
                        queryParams.append(key, String(values[key]));
                    }
                }
            });

            const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
            const res = await fetch(`${API_BASE_URL}/partner-applications/tn/${id}/docx?${queryParams.toString()}`, {
                method: 'GET',
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.error || 'Ошибка экспорта DOCX');
            }

            const blob = await res.blob();
            const contentDisposition = res.headers.get('Content-Disposition');
            let filename = `Заявка партнера ${id}.docx`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)$/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = decodeURIComponent(filenameMatch[1]);
                } else {
                    const basicFilenameMatch = contentDisposition.match(/filename="([^"]+)"/);
                    if (basicFilenameMatch && basicFilenameMatch[1]) {
                        filename = basicFilenameMatch[1];
                    }
                }
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            message.success('Заявка успешно экспортирована в DOCX');
            setPartnerModalOpen(false);
        } catch (error: any) {
            message.error(error.message || 'Ошибка при экспорте заявки');
            console.error('Export error:', error);
        }
    };
    const autosaveTimer = useRef<number | null>(null);
    const isSavingRef = useRef(false);
    const scheduleAutosaveRef = useRef<() => void>(() => {});
	const [createClientOpen, setCreateClientOpen] = useState(false);
	const [createShipperOpen, setCreateShipperOpen] = useState(false);
	const [createConsigneeOpen, setCreateConsigneeOpen] = useState(false);
	const [createCarrierOpen, setCreateCarrierOpen] = useState(false);
	const [createDriverOpen, setCreateDriverOpen] = useState(false);

	// Добавление груза из склада/заявки
	const [addCargoOpen, setAddCargoOpen] = useState(false);
	const [cargoSourceType, setCargoSourceType] = useState<'warehouse' | 'application'>('application');
	const [cargoSourceId, setCargoSourceId] = useState<number | undefined>(undefined);
	const [selectedCargoIds, setSelectedCargoIds] = useState<number[]>([]);
	const { useGetAvailableCargoQuery } = routesApi;
	const { data: warehouses } = useGetWarehousesQuery();
	const { useGetApplicationsQuery } = applicationsApi;
	const { data: applicationsList } = useGetApplicationsQuery({ page: 1, limit: 50, search: '' } as any, { skip: !addCargoOpen });
	const { data: availableCargo } = useGetAvailableCargoQuery(
		{ loadingPointType: cargoSourceType, loadingPointId: cargoSourceId as any },
		{ skip: !addCargoOpen || !cargoSourceId }
	);

    const didInitRef = React.useRef(false);
    React.useEffect(() => {
        if (!tn || didInitRef.current) return;
        const hasPrefix = !!(tn as any).consigneeNamePrefix;
        form.setFieldsValue({
				number: tn.number,
				date: dayjs(tn.date).format('YYYY-MM-DD'),
				shipperId: tn.shipperId,
				consigneeId: tn.consigneeId,
				clientId: (tn as any)?.clientId ?? undefined,
				carrierId: tn.carrierId,
				driverId: tn.driverId,
				vehicleId: tn.vehicleId,
				trailerVehicleId: tn.trailerVehicleId ?? undefined,
				loadingAddress: tn.loadingAddress,
				unloadingAddress: tn.unloadingAddress,
				freightAmount: tn.freightAmount,
				useConsigneePrefix: hasPrefix,
				consigneeNamePrefix: (tn as any).consigneeNamePrefix || undefined,
				accompanyingDocuments: (tn as any).accompanyingDocuments || undefined,
				p1IsExpeditor: (tn as any).p1IsExpeditor || false,
				p8ShipperId: (tn as any).p8ShipperId || undefined,
				p12Empty: (tn as any).p12Empty || false,
			});
        setItems((tn.items || []).map((i: any) => ({
				cargoId: i.cargoId as any,
				name: (i as any).name,
				places: Number((i as any).places || 0),
				weightKg: (i as any).weightKg ? Number((i as any).weightKg) : undefined,
				volumeM3: (i as any).volumeM3 ? Number((i as any).volumeM3) : undefined,
				packageType: (i as any).packageType || undefined,
				notes: (i as any).notes || undefined,
			})));
        didInitRef.current = true;
    }, [tn, form]);

	// prevDriver: при открытии существующей ТН, если в форме был водитель
	useEffect(() => {
		if (isNew || !tn?.id) return;
		if (!tn.driverId) {
			if (prevDriverSyncedForTnId.current !== tn.id) {
				setPrevDriver(null);
				prevDriverSyncedForTnId.current = tn.id;
			}
			return;
		}
		if (!drivers?.length) return;
		if (prevDriverSyncedForTnId.current === tn.id) return;
		setPrevDriver(drivers.find((d) => d.id === tn.driverId) ?? null);
		prevDriverSyncedForTnId.current = tn.id;
	}, [isNew, tn?.id, tn?.driverId, drivers]);

    // Создание черновика сразу при открытии страницы "Новая ТН"
    React.useEffect(() => {
        if (isNew) {
            (async () => {
                try {
                    const created = await createTN({} as any).unwrap();
                    const newId = (created as any)?.id;
                    if (newId) navigate(`/tn/${newId}`);
                } catch (e) {
                    // игнорируем — пользователь может вернуться к списку
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNew]);

	function addItem() {
		setItems(prev => [...prev, { name: '', places: 0 }]);
	}
	function duplicateItem(index: number) {
		setItems(prev => {
			const copy = [...prev];
			copy.splice(index + 1, 0, { ...prev[index] });
			return copy;
		});
	}
	function removeItem(index: number) {
		setItems(prev => prev.filter((_, i) => i !== index));
	}
    function scheduleAutosave() {
        if (isViewMode) return;
        if (!id || !Number.isFinite(id)) return;
        if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
        autosaveTimer.current = window.setTimeout(async () => {
            try {
                isSavingRef.current = true;
                const values = form.getFieldsValue();
                const payload = {
                    number: values.number,
                    date: values.date?.toISOString?.() || values.date,
                    shipperId: values.shipperId,
                    p1IsExpeditor: values.p1IsExpeditor,
                    p8ShipperId: values.p8ShipperId,
                    p12Empty: values.p12Empty,
                    consigneeId: values.consigneeId,
                    clientId: values.clientId,
                    carrierId: values.carrierId,
                    driverId: values.driverId,
                    vehicleId: values.vehicleId,
                    trailerVehicleId: values.trailerVehicleId,
                    loadingAddress: values.loadingAddress,
                    unloadingAddress: values.unloadingAddress,
                    freightAmount: values.freightAmount,
                    consigneeNamePrefix: values.useConsigneePrefix ? values.consigneeNamePrefix : null,
                    accompanyingDocuments: values.accompanyingDocuments || null,
                    items
                } as any;
                // не отправляем undefined, чтобы не затирать значения на бэкенде
                Object.keys(payload).forEach((k) => (payload as any)[k] === undefined && delete (payload as any)[k]);
                await updateTN({ id, data: payload }).unwrap();
            } catch {}
            finally { isSavingRef.current = false; }
        }, 1200);
    }
    scheduleAutosaveRef.current = scheduleAutosave;

    const updateItem = useCallback((index: number, data: Partial<TNItemDto>) => {
        setItems(prev => prev.map((it, i) => i === index ? { ...it, ...data } : it));
        scheduleAutosaveRef.current();
    }, []);

	const columns: ColumnsType<TNItemDto> = useMemo(() => {
		const cols: ColumnsType<TNItemDto> = [
		{ title: 'Наименование', dataIndex: 'name', key: 'name', render: (v, r, idx) => (
			<div>
				{isViewMode ? (
					<Text>{v ?? '—'}</Text>
				) : (
					<Input value={v} onChange={e => updateItem(idx, { name: e.target.value })} />
				)}
				{(r as any).applicationInternalNumber && (
					<div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
						{(r as any).applicationInternalNumber}{(r as any).applicationClientName ? ` — ${(r as any).applicationClientName}` : ''}
					</div>
				)}
			</div>
		)},
		{ title: 'Места', dataIndex: 'places', key: 'places', width: 100, render: (v, r, idx) => (
			isViewMode ? <Text>{v ?? '—'}</Text> : (
				<InputNumber min={0} value={v} onChange={val => updateItem(idx, { places: Number(val || 0) })} />
			)
		)},
		{ title: 'Вес (кг)', dataIndex: 'weightKg', key: 'weightKg', width: 120, render: (v, r, idx) => (
			isViewMode ? <Text>{v ?? '—'}</Text> : (
				<InputNumber min={0} value={v} onChange={val => updateItem(idx, { weightKg: Number(val || 0) })} />
			)
		)},
		{ title: 'Объем (м³)', dataIndex: 'volumeM3', key: 'volumeM3', width: 120, render: (v, r, idx) => (
			isViewMode ? <Text>{v ?? '—'}</Text> : (
				<InputNumber min={0} value={v} onChange={val => updateItem(idx, { volumeM3: Number(val || 0) })} />
			)
		)},
		{ title: 'Упаковка', dataIndex: 'packageType', key: 'packageType', width: 140, render: (v, r, idx) => (
			isViewMode ? <Text>{v ?? '—'}</Text> : (
				<Input value={v} onChange={e => updateItem(idx, { packageType: e.target.value })} />
			)
		)},
		{ title: 'Примечание', dataIndex: 'notes', key: 'notes', render: (v, r, idx) => (
			isViewMode ? <Text>{v ?? '—'}</Text> : (
				<Input value={v} onChange={e => updateItem(idx, { notes: e.target.value })} />
			)
		)},
		];
		if (!isViewMode) {
			cols.push({
				title: 'Действия', key: 'actions', width: 120, render: (_, __, idx) => (
					<Space>
						<Button size="small" onClick={() => duplicateItem(idx)}>Дублировать</Button>
						<Button size="small" danger onClick={() => removeItem(idx)}>Удалить</Button>
					</Space>
				)
			});
		}
		return cols;
	}, [isViewMode, updateItem]);

	const totals = useMemo(() => {
		const places = items.reduce((s, i) => s + (Number(i.places) || 0), 0);
		const weight = items.reduce((s, i) => s + (Number(i.weightKg || 0)), 0);
		const volume = items.reduce((s, i) => s + (Number(i.volumeM3 || 0)), 0);
		return { places, weight, volume };
	}, [items]);

	const uploadProps: UploadProps = {
		action: `${API_BASE_URL}/tn/${id}/xlsx/import`,
		name: 'file',
		showUploadList: false,
		withCredentials: true,
		headers: (() => {
			const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
			return token ? { Authorization: `Bearer ${token}` } : undefined;
		})(),
		onChange(info) {
			if (info.file.status === 'done') {
				message.success('Excel импортирован');
				refetch();
			} else if (info.file.status === 'error') {
				message.error('Ошибка импорта');
			}
		}
	};

	// Сохранение заявки партнёра в БД
	const handlePartnerSave = async () => {
		try {
			const values = await partnerForm.validateFields();
			const body: any = {
				tnId: id,
				applicationDate: dayjs.isDayjs(values.applicationDate) ? values.applicationDate.format('YYYY-MM-DD') : values.applicationDate,
				applicationNumber: values.applicationNumber,
				customerName: values.customerName,
				performerName: values.performerName,
				shipperName: values.shipperName,
				consigneeName: values.consigneeName,
				loadingAddress: values.loadingAddress,
				unloadingAddress: values.unloadingAddress,
				contactPersonLoading: values.contactPersonLoading,
				contactPhoneLoading: values.contactPhoneLoading,
				contactPersonUnloading: values.contactPersonUnloading,
				contactPhoneUnloading: values.contactPhoneUnloading,
				rate: values.rate ? Number(values.rate) : null,
			};
			const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
			const res = await fetch(`${API_BASE_URL}/partner-applications`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				credentials: 'include',
				body: JSON.stringify(body),
			});
			const data = await res.json();
			if (!res.ok || !data.success) throw new Error(data.error || 'Ошибка сохранения');
			message.success('Заявка сохранена');
		} catch (error: any) {
			message.error(error.message || 'Ошибка сохранения заявки');
		}
	};

	async function handleSave(values: any) {
		if (isViewMode) return;
		try {
			if (isNew) {
				const created = await createTN({
					number: values.number || null,
					date: values.date?.toISOString?.() || values.date,
					shipperId: values.shipperId,
					p1IsExpeditor: values.p1IsExpeditor,
					p8ShipperId: values.p8ShipperId,
					p12Empty: values.p12Empty,
					consigneeId: values.consigneeId,
					clientId: values.clientId,
					carrierId: values.carrierId,
					driverId: values.driverId,
					vehicleId: values.vehicleId,
					trailerVehicleId: values.trailerVehicleId,
					loadingAddress: values.loadingAddress,
					unloadingAddress: values.unloadingAddress,
					freightAmount: values.freightAmount,
					consigneeNamePrefix: values.useConsigneePrefix ? values.consigneeNamePrefix : null,
					accompanyingDocuments: values.accompanyingDocuments || null,
					items
				}).unwrap();
				message.success('Создано');
				const newId = (created as any)?.id;
				if (newId) navigate(`/tn/${newId}`);
				return;
			}
			await updateTN({ id, data: {
				number: values.number,
				date: values.date?.toISOString?.() || values.date,
				shipperId: values.shipperId,
				consigneeId: values.consigneeId,
				clientId: values.clientId,
				carrierId: values.carrierId,
				driverId: values.driverId,
				vehicleId: values.vehicleId,
				trailerVehicleId: values.trailerVehicleId,
				loadingAddress: values.loadingAddress,
				unloadingAddress: values.unloadingAddress,
				freightAmount: values.freightAmount,
				consigneeNamePrefix: values.useConsigneePrefix ? values.consigneeNamePrefix : null,
				accompanyingDocuments: values.accompanyingDocuments || null,
				p12Empty: values.p12Empty,
				items
			} }).unwrap();
			message.success('Сохранено');
			refetch();
		} catch (e: any) {
			message.error(e?.data?.error || 'Ошибка сохранения');
		}
	}

	async function exportTn() {
		try {
			const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
			const res = await fetch(`${API_BASE_URL}/tn/${id}/xlsx`, {
				headers: token ? { Authorization: `Bearer ${token}` } : undefined,
				credentials: 'include'
			});
			if (!res.ok) {
				message.error('Ошибка экспорта');
				return;
			}
			// Извлекаем имя из заголовка Content-Disposition
			const cd = res.headers.get('Content-Disposition') || '';
			let filename = `tn_${id}.xlsx`;
			const m = cd.match(/filename\*?=UTF-8''([^;]+)|filename="?([^;"]+)"?/i);
			if (m) filename = decodeURIComponent(m[1] || m[2]);

			const blob = await res.blob();
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
		} catch (err) {
			message.error('Ошибка экспорта');
		}
	}

	const handleExport = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		e.stopPropagation();
		void exportTn();
	};

	const addSelectedCargoToItems = () => {
		const map = new Map<number, any>();
		(availableCargo || []).forEach((c: any) => map.set(c.id, c));
		const toAdd: TNItemDto[] = selectedCargoIds
			.map(id => map.get(id))
			.filter(Boolean)
			.map((c: any) => ({
				cargoId: c.id,
				name: c.name,
				places: Number(c.quantity || 0),
				weightKg: c.totalWeightKg ? Number(c.totalWeightKg) : undefined,
				volumeM3: c.volumeM3 ? Number(c.volumeM3) : undefined,
				packageType: c.packagingType || undefined,
				// preserve application info for display
				applicationInternalNumber: c.application?.internalNumber || undefined,
				applicationClientName: c.application?.client?.name || undefined
			}));
		if (toAdd.length === 0) {
			message.warning('Не выбраны грузы');
			return;
		}
		setItems(prev => [...prev, ...toAdd]);
		setAddCargoOpen(false);
		setSelectedCargoIds([]);
	};

	return (
		<div style={{ padding: 24 }}>
			<Title level={3} style={{ color: 'var(--text)' }}>
				{isViewMode ? 'Просмотр ТН' : 'Редактор ТН'} {isNew ? '' : `#${id}`}
			</Title>
			<Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }} loading={isLoading}>
                <Form form={form} layout="vertical" onFinish={handleSave} onValuesChange={isViewMode ? undefined : scheduleAutosave} disabled={isViewMode}>
					<Row gutter={16}>
						<Col span={6}>
							<Form.Item name="number" label="Номер">
								<Input />
							</Form.Item>
						</Col>
						<Col span={6}>
							<Form.Item name="date" label="Дата" rules={[{ required: true }]}>
								<Input type="date" />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col span={6}>
					<Form.Item name="shipperId" label="Грузоотправитель (п.1)">
						<SmartSelect
							allowClear
							options={(shippers || []).map(s => ({ value: s.id, label: s.name }))}
							dropdownRender={menu => (
								<div>
									{menu}
									<div style={{ display: 'flex', padding: 8 }}>
										<Button type="link" onClick={() => setCreateShipperOpen(true)}>+ Добавить грузоотправителя</Button>
									</div>
								</div>
							)}
						/>
					</Form.Item>
					<Form.Item name="p1IsExpeditor" valuePropName="checked" label="Является экспедитором (п.1)">
						<Checkbox />
					</Form.Item>
						</Col>
						<Col span={6}>
					<Form.Item name="consigneeId" label="Грузополучатель">
						<SmartSelect
							allowClear
							options={(consignees || []).map(s => ({ value: s.id, label: s.name }))}
							dropdownRender={menu => (
								<div>
									{menu}
									<div style={{ display: 'flex', padding: 8 }}>
										<Button type="link" onClick={() => setCreateConsigneeOpen(true)}>+ Добавить грузополучателя</Button>
									</div>
								</div>
							)}
						/>
					</Form.Item>
					<Form.Item name="p8ShipperId" label="Грузоотправитель (п.8)">
						<SmartSelect
							allowClear
							options={(shippers || []).map(s => ({ value: s.id, label: s.name }))}
						/>
					</Form.Item>
						</Col>
						<Col span={6}>
							<Form.Item name="clientId" label="Заказчик / Клиент">
								<SmartSelect
									allowClear
									options={(clients || []).map(c => ({ value: c.id, label: c.name }))}
									dropdownRender={menu => (
										<div>
											{menu}
											<div style={{ display: 'flex', padding: 8 }}>
												<Button type="link" onClick={() => setCreateClientOpen(true)}>+ Добавить клиента</Button>
											</div>
										</div>
									)}
								/>
							</Form.Item>
						</Col>
					<Col span={12}>
						<Form.Item name="useConsigneePrefix" valuePropName="checked" label="Использовать префикс перед грузополучателем">
							<Checkbox />
						</Form.Item>
						<Form.Item shouldUpdate={(prev: any, curr: any) => prev?.useConsigneePrefix !== curr?.useConsigneePrefix} noStyle>
							{({ getFieldValue }) => {
								const usePrefix = getFieldValue('useConsigneePrefix');
								return usePrefix ? (
									<Form.Item name="consigneeNamePrefix" label="Префикс перед грузополучателем">
										<Input placeholder='Например: ООО "ТАЙКАН", ИНН 5407982092 для' />
									</Form.Item>
								) : null;
							}}
						</Form.Item>
						<Form.Item name="p12Empty" valuePropName="checked" label="12 пункт пустой">
							<Checkbox />
						</Form.Item>
					</Col>
					</Row>

					<Row gutter={16}>
						<Col span={8}>
							<Form.Item name="carrierId" label="Перевозчик">
								<SmartSelect
									allowClear
									open={carrierDropdownOpen}
									onDropdownVisibleChange={(open) => setCarrierDropdownOpen(open)}
									options={(carriers || []).map(c => ({ value: c.id, label: c.name }))}
									dropdownRender={menu => (
										<div>
											{menu}
											<div style={{ display: 'flex', padding: 8 }}>
												<Button type="link" onClick={() => { setCarrierDropdownOpen(false); setCreateCarrierOpen(true); }}>+ Добавить перевозчика</Button>
											</div>
										</div>
									)}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={8}>
							<Form.Item name="driverId" label="Водитель">
								<SmartSelect
									allowClear
									open={driverDropdownOpen}
									onDropdownVisibleChange={(open) => setDriverDropdownOpen(open)}
									options={(drivers || []).map((d) => ({ value: d.id, label: d.fullName }))}
									onChange={(val: number | null) => {
										const currentTruckId = form.getFieldValue('vehicleId') as number | undefined;
										const currentTrailerId = form.getFieldValue('trailerVehicleId') as number | undefined;
										if (val == null) {
											setPrevDriver(null);
											form.setFieldsValue({
												driverId: undefined,
												vehicleId: undefined,
												trailerVehicleId: undefined
											});
											return;
										}
										const newDr = (drivers || []).find((dr) => dr.id === val);
										const { vehicleId: nextTruck, trailerVehicleId: nextTrailer } =
											computeNextVehiclesFromDriverChange(prevDriver, newDr, currentTruckId, currentTrailerId);
										form.setFieldsValue({
											driverId: val,
											vehicleId: nextTruck,
											trailerVehicleId: nextTrailer
										});
										setPrevDriver(newDr ?? null);
									}}
									dropdownRender={menu => (
										<div>
											{menu}
											<div style={{ display: 'flex', padding: 8 }}>
												<Button type="link" onClick={() => { setDriverDropdownOpen(false); setCreateDriverOpen(true); }}>+ Добавить водителя</Button>
											</div>
										</div>
									)}
								/>
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="vehicleId"
								label="Тягач"
								rules={isViewMode ? [] : [{ required: true, message: 'Укажите тягач' }]}
							>
								<VehicleSelectInline
									vehicles={trucks}
									placeholder="Выберите тягач"
									allowClear={false}
									modalCategory="TRUCK"
									addButtonTooltip="Добавить тягач"
									addButtonAriaLabel="Добавить тягач"
									onRefetchVehicles={refetchVehicles}
								/>
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item name="trailerVehicleId" label="Прицеп">
								<VehicleSelectInline
									vehicles={trailers}
									placeholder="Выберите прицеп"
									allowClear
									modalCategory="TRAILER"
									addButtonTooltip="Добавить прицеп"
									addButtonAriaLabel="Добавить прицеп"
									onRefetchVehicles={refetchVehicles}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col span={12}>
							<Form.Item name="loadingAddress" label="Адрес погрузки">
								<AddressAutocomplete placeholder="Начните вводить адрес..." />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="unloadingAddress" label="Адрес выгрузки">
								<AddressAutocomplete placeholder="Начните вводить адрес..." />
							</Form.Item>
						</Col>
					</Row>

					<Form.Item name="freightAmount" label="Ставка перевозки, ₽">
						<InputNumber min={0} style={{ width: 240 }} />
					</Form.Item>

					<Form.Item name="accompanyingDocuments" label="Сопроводительные документы">
						<Input.TextArea rows={3} placeholder="Укажите сопроводительные документы (накладные, сертификаты и т.д.)" />
					</Form.Item>

					<Space align="baseline" style={{ marginBottom: 8 }}>
						<Title level={5} style={{ margin: 0 }}>Позиции</Title>
						{!isViewMode && (
							<>
								<Button onClick={addItem}>Добавить</Button>
								<Button onClick={() => setAddCargoOpen(true)}>Добавить из заявки/склада</Button>
							</>
						)}
						<Text type="secondary">Итого: мест {totals.places}, вес {totals.weight.toFixed(2)}, объем {totals.volume.toFixed(2)}</Text>
					</Space>
					<DataTable
						columns={columns}
						dataSource={items}
						rowKey={(_r, i) => String(i)}
						pagination={false}
						size="small"
						scroll={{ y: 280 }}
					/>

                    <Space style={{ marginTop: 16 }}>
                        {!isViewMode && <Button type="primary" htmlType="submit">Сохранить</Button>}
						<Button htmlType="button" onClick={() => navigate('/tn')}>К списку</Button>
						{!isNew && <Button htmlType="button" onClick={handleExport}>Экспорт в Excel</Button>}
						{!isViewMode && (
							<>
								<Upload {...uploadProps}><Button htmlType="button">Импорт из Excel</Button></Upload>
								{!isNew && (
									<Button htmlType="button" onClick={() => {
										// Предзаполнить и открыть модалку заявки партнёра
										const t: any = tn as any;
										const p8Name = (shippers || []).find((s:any) => s.id === (t?.p8ShipperId))?.name;
										partnerForm.setFieldsValue({
											clientName: t?.client?.name,
											clientInn: t?.client?.inn,
											shipperName: p8Name || t?.shipper?.name,
											consigneeName: t?.consignee?.name,
											loadingAddress: t?.loadingAddress,
											unloadingAddress: t?.unloadingAddress,
											contactPerson: '',
											contactPhone: '',
											rate: t?.route?.totalAmount || t?.freightAmount || undefined,
										});
										setPartnerModalOpen(true);
									}}>Сформировать заявку</Button>
								)}
							</>
						)}
					</Space>
				</Form>
            </Card>

            <Modal
              title="Заявка партнёра"
              open={partnerModalOpen}
              onCancel={() => setPartnerModalOpen(false)}
              okText="Экспорт DOCX"
              afterOpenChange={(open) => {
                if (!open) return;
                // Инициализируем форму дефолтными значениями
                const baseDate = tn?.date ? dayjs(tn.date) : dayjs();
                const dayMonth = baseDate.format('DDMM');
                partnerForm.setFieldsValue({
                  applicationDate: baseDate,
                  applicationNumber: `${dayMonth}/1`,
                  customerName: 'ООО «ТАЙКАН»',
                  performerName: 'ООО «ЮМИГА»',
                  shipperName: partnerForm.getFieldValue('shipperName') || '',
                  consigneeName: tn?.consignee?.name || '',
                  loadingAddress: tn?.loadingAddress || '',
                  unloadingAddress: tn?.unloadingAddress || '',
                  rate: tn?.freightAmount || '',
                });
                // Сразу подтянем следующий номер по дате ТН
                void handleDateChange(baseDate);
              }}
				footer={[
                <Button key="back" onClick={() => setPartnerModalOpen(false)}>
                  Отмена
                </Button>,
					<Button key="save" onClick={handlePartnerSave}>
						Сохранить заявку
					</Button>,
                <Button key="submit" type="primary" onClick={handlePartnerDocxExport}>
                  Экспорт DOCX
                </Button>,
              ]}
            >
              <Form form={partnerForm} layout="vertical">
                <Typography.Title level={5}>Данные заявки</Typography.Title>
                
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="applicationDate" label="Дата заявки" rules={[{ required: true, message: 'Введите дату заявки' }]}>
                      <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} onChange={handleDateChange} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="applicationNumber" label="Номер заявки" rules={[{ required: true, message: 'Введите номер заявки' }]}>
                      <Input placeholder="1310/1" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Typography.Title level={5} style={{ marginTop: 16 }}>Стороны договора</Typography.Title>
                
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="customerName" label="Заказчик (партнёр)" initialValue="ООО «ТАЙКАН»">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="performerName" label="Исполнитель (наша компания)" initialValue="ООО «ЮМИГА»">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Typography.Title level={5} style={{ marginTop: 16 }}>Данные по грузу</Typography.Title>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="shipperName" label="Грузоотправитель">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="consigneeName" label="Грузополучатель">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="loadingAddress" label="Место загрузки">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="unloadingAddress" label="Место выгрузки">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Typography.Title level={5} style={{ marginTop: 16 }}>Контактные лица</Typography.Title>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="contactPersonLoading" label="Контактное лицо (загрузка)">
                      <Input placeholder="ФИО ответственного" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="contactPhoneLoading" label="Телефон (загрузка)">
                      <Input placeholder="+7 (xxx) xxx-xx-xx" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="contactPersonUnloading" label="Контактное лицо (выгрузка)">
                      <Input placeholder="ФИО ответственного" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="contactPhoneUnloading" label="Телефон (выгрузка)">
                      <Input placeholder="+7 (xxx) xxx-xx-xx" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="rate" label="Ставка, руб.">
                      <Input placeholder="Ставка перевозки" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
                  Номер заявки формируется автоматически (ДДММ/1 от ДД.ММ.ГГГГ).<br/>
                  Позиции груза, вес, объём и количество мест будут автоматически взяты из ТН.
                </Typography.Paragraph>
              </Form>
            </Modal>

			{/* Модалки создания справочных сущностей */}
			<CreateContractorModal isOpen={createClientOpen} onCancel={() => setCreateClientOpen(false)} onSuccess={(c:any)=>{ setCreateClientOpen(false); form.setFieldsValue({ clientId: c.id }); }} defaultRoles={CONTRACTOR_DEFAULT_ROLES_CLIENT} />
			<CreateContractorModal isOpen={createShipperOpen} onCancel={() => setCreateShipperOpen(false)} onSuccess={(c:any)=>{ setCreateShipperOpen(false); form.setFieldsValue({ shipperId: c.id }); }} defaultRoles={CONTRACTOR_DEFAULT_ROLES_SHIPPER} />
			<CreateContractorModal isOpen={createConsigneeOpen} onCancel={() => setCreateConsigneeOpen(false)} onSuccess={(c:any)=>{ setCreateConsigneeOpen(false); form.setFieldsValue({ consigneeId: c.id }); }} defaultRoles={CONTRACTOR_DEFAULT_ROLES_CONSIGNEE} />
			<CreateCarrierModal isOpen={createCarrierOpen} onClose={() => setCreateCarrierOpen(false)} onCarrierCreated={(c:any)=>{ setCreateCarrierOpen(false); form.setFieldsValue({ carrierId: c.id }); }} />
			<CreateDriverModal
				isOpen={createDriverOpen}
				onClose={() => setCreateDriverOpen(false)}
				onDriverCreated={(d:any)=>{
					setCreateDriverOpen(false);
					setDriverDropdownOpen(false);
					const currentTruckId = form.getFieldValue('vehicleId') as number | undefined;
					const currentTrailerId = form.getFieldValue('trailerVehicleId') as number | undefined;
					const { vehicleId: nextTruck, trailerVehicleId: nextTrailer } = computeNextVehiclesFromDriverChange(
						prevDriver,
						d,
						currentTruckId,
						currentTrailerId
					);
					setPrevDriver(d);
					form.setFieldsValue({
						driverId: d.id,
						vehicleId: nextTruck,
						trailerVehicleId: nextTrailer
					});
				}}
			/>

			{/* Модалка добавления грузов из заявки/склада */}
			<Modal open={addCargoOpen} onCancel={()=>setAddCargoOpen(false)} onOk={addSelectedCargoToItems} okText="Добавить выбранные" title="Добавить груз из заявки/склада">
				<Space direction="vertical" style={{ width: '100%' }}>
					<Radio.Group value={cargoSourceType} onChange={e=>{ setCargoSourceType(e.target.value); setCargoSourceId(undefined); setSelectedCargoIds([]); }}>
						<Radio.Button value="application">Из заявки</Radio.Button>
						<Radio.Button value="warehouse">Со склада</Radio.Button>
					</Radio.Group>
					{cargoSourceType === 'application' ? (
						<SmartSelect
							placeholder="Выберите заявку"
							style={{ width: 360 }}
							value={cargoSourceId}
							onChange={(v)=> setCargoSourceId(v as number)}
							options={((applicationsList as any)?.applications || []).map((a: any) => ({
								value: a.id,
								label: `${a.internalNumber || `Заявка #${a.id}`}${a.client?.name ? ` — ${a.client.name}` : ''}`
							}))}
							sortBy="alphabetical"
						/>
					) : (
						<SmartSelect
							placeholder="Выберите склад"
							style={{ width: 360 }}
							value={cargoSourceId}
							onChange={(v)=> setCargoSourceId(v as number)}
							options={(warehouses || []).map((w: any) => ({ value: w.id, label: w.name }))}
							sortBy="alphabetical"
						/>
					)}
					<DataTable
						dataSource={(availableCargo as any) || []}
						size="small"
						pagination={{ pageSize: 8 }}
						scroll={{ y: 320 }}
						rowSelection={{
							selectedRowKeys: selectedCargoIds,
							onChange: (keys: React.Key[]) => setSelectedCargoIds(keys as number[])
						}}
						columns={[
							{ title: 'ID', dataIndex: 'id', width: 80 },
							{ title: 'Наименование', dataIndex: 'name' },
							{ title: 'Кол-во', dataIndex: 'quantity', width: 100 },
							{ title: 'Вес, кг', dataIndex: 'totalWeightKg', width: 120 },
							{
                title: 'Заявка',
                key: 'app',
                width: 200,
                render: (_: unknown, r: { application?: { id?: number; internalNumber?: string; externalNumber?: string; applicationDate?: string; number?: string } }) => {
                  const a = r.application;
                  if (!a) return '—';
                  return (
                    <div onClick={(e) => e.stopPropagation()}>
                      <ApplicationCell
                        withLink={a.id != null}
                        application={
                          a.id != null
                            ? {
                                id: a.id,
                                internalNumber: a.internalNumber ?? null,
                                number: a.number,
                                applicationDate: a.applicationDate ?? null
                              }
                            : {
                                internalNumber: a.internalNumber ?? null,
                                number: a.number,
                                applicationDate: a.applicationDate ?? null
                              }
                        }
                      />
                    </div>
                  );
                },
              },
						]}
					/>
				</Space>
			</Modal>
		</div>
	);
};

export default TNEditPage;
